/* eslint-disable no-unused-vars */
import { nextTick, ref, watch } from 'vue'

import { BSnackbarProps } from '../components/BSnackbar/use-snackbar'

interface Snackbar extends BSnackbarProps {
	'click:action'?: (event: MouseEvent) => void
	'click:close'?: (event: MouseEvent) => void
	'update:modelValue'?: (value: boolean) => void
}

let _snackbarInstance: ReturnType<typeof createSnackbar> | null = null

function createSnackbar() {
	const stack = ref<Snackbar[]>([])
	const current = ref<Snackbar | null>(null)
	const isVisible = ref(false)

	watch(
		() => stack.value.length,
		(value, oldValue) => {
			if (!isVisible.value && value > oldValue) {
				showNext()
			}
		}
	)

	function show(snackbar: Snackbar) {
		// prevent duplicated messages
		if (
			(current.value && current.value.title === snackbar.title) ||
			(stack.value.length &&
				stack.value.find((item) => item.title === snackbar.title))
		) {
			return
		}

		stack.value.push({ duration: 5000, ...snackbar })
	}

	function success(snackbar: Snackbar) {
		const successConfig: Snackbar = {
			color: 'success',
			icon: 'checkmark-circle-01',
			advanced: !!snackbar.title && !!snackbar.description,
			...snackbar
		}
		show(successConfig)
	}

	function error(snackbar: Snackbar) {
		const errorConfig: Snackbar = {
			color: 'error',
			icon: 'cancel-circle',
			advanced: !!snackbar.title && !!snackbar.description,
			...snackbar
		}
		show(errorConfig)
	}

	function warning(snackbar: Snackbar) {
		const warningConfig: Snackbar = {
			color: 'warning',
			icon: 'alert-circle',
			advanced: !!snackbar.title && !!snackbar.description,
			...snackbar
		}
		show(warningConfig)
	}

	function inform(snackbar: Snackbar) {
		const informConfig: Snackbar = {
			color: 'inform',
			icon: 'information-circle',
			advanced: !!snackbar.title && !!snackbar.description,
			...snackbar
		}
		show(informConfig)
	}

	function showNext() {
		const [next, ...rest] = stack.value
		stack.value = rest
		current.value = next
		nextTick(() => {
			isVisible.value = true
		})
	}

	function onAfterLeave() {
		if (stack.value.length) {
			showNext()
		} else {
			isVisible.value = false
			current.value = null
		}
	}

	return {
		show,
		success,
		error,
		warning,
		inform,
		isVisible,
		current,
		stack,
		onAfterLeave
	}
}

export function useSnackbar() {
	if (!_snackbarInstance) {
		_snackbarInstance = createSnackbar()
	}
	return _snackbarInstance
}
